/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { Component, ViewEncapsulation, Renderer2, ElementRef, Input, ContentChildren, Directive, ChangeDetectionStrategy, } from '@angular/core';
import { MatLine, MatLineSetter } from '@angular/material/core';
import { coerceToNumber } from './grid-list-measure';
export class MatGridTile {
    /**
     * @param {?} _renderer
     * @param {?} _element
     */
    constructor(_renderer, _element) {
        this._renderer = _renderer;
        this._element = _element;
        this._rowspan = 1;
        this._colspan = 1;
    }
    /**
     * Amount of rows that the grid tile takes up.
     * @return {?}
     */
    get rowspan() { return this._rowspan; }
    /**
     * @param {?} value
     * @return {?}
     */
    set rowspan(value) { this._rowspan = coerceToNumber(value); }
    /**
     * Amount of columns that the grid tile takes up.
     * @return {?}
     */
    get colspan() { return this._colspan; }
    /**
     * @param {?} value
     * @return {?}
     */
    set colspan(value) { this._colspan = coerceToNumber(value); }
    /**
     * Sets the style of the grid-tile element.  Needs to be set manually to avoid
     * "Changed after checked" errors that would occur with HostBinding.
     * @param {?} property
     * @param {?} value
     * @return {?}
     */
    _setStyle(property, value) {
        this._renderer.setStyle(this._element.nativeElement, property, value);
    }
}
MatGridTile.decorators = [
    { type: Component, args: [{selector: 'mat-grid-tile',
                exportAs: 'matGridTile',
                host: {
                    'class': 'mat-grid-tile',
                },
                template: "<figure class=\"mat-figure\"><ng-content></ng-content></figure>",
                styles: [".mat-grid-list{display:block;position:relative}.mat-grid-tile{display:block;position:absolute;overflow:hidden}.mat-grid-tile .mat-figure{top:0;left:0;right:0;bottom:0;position:absolute;display:flex;align-items:center;justify-content:center;height:100%;padding:0;margin:0}.mat-grid-tile .mat-grid-tile-footer,.mat-grid-tile .mat-grid-tile-header{display:flex;align-items:center;height:48px;color:#fff;background:rgba(0,0,0,.38);overflow:hidden;padding:0 16px;position:absolute;left:0;right:0}.mat-grid-tile .mat-grid-tile-footer>*,.mat-grid-tile .mat-grid-tile-header>*{margin:0;padding:0;font-weight:400;font-size:inherit}.mat-grid-tile .mat-grid-tile-footer.mat-2-line,.mat-grid-tile .mat-grid-tile-header.mat-2-line{height:68px}.mat-grid-tile .mat-grid-list-text{display:flex;flex-direction:column;width:100%;box-sizing:border-box;overflow:hidden}.mat-grid-tile .mat-grid-list-text>*{margin:0;padding:0;font-weight:400;font-size:inherit}.mat-grid-tile .mat-grid-list-text:empty{display:none}.mat-grid-tile .mat-grid-tile-header{top:0}.mat-grid-tile .mat-grid-tile-footer{bottom:0}.mat-grid-tile .mat-grid-avatar{padding-right:16px}[dir=rtl] .mat-grid-tile .mat-grid-avatar{padding-right:0;padding-left:16px}.mat-grid-tile .mat-grid-avatar:empty{display:none}"],
                encapsulation: ViewEncapsulation.None,
                preserveWhitespaces: false,
                changeDetection: ChangeDetectionStrategy.OnPush,
            },] },
];
/**
 * @nocollapse
 */
MatGridTile.ctorParameters = () => [
    { type: Renderer2, },
    { type: ElementRef, },
];
MatGridTile.propDecorators = {
    'rowspan': [{ type: Input },],
    'colspan': [{ type: Input },],
};
function MatGridTile_tsickle_Closure_declarations() {
    /** @type {?} */
    MatGridTile.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatGridTile.ctorParameters;
    /** @type {?} */
    MatGridTile.propDecorators;
    /** @type {?} */
    MatGridTile.prototype._rowspan;
    /** @type {?} */
    MatGridTile.prototype._colspan;
    /** @type {?} */
    MatGridTile.prototype._renderer;
    /** @type {?} */
    MatGridTile.prototype._element;
}
export class MatGridTileText {
    /**
     * @param {?} _renderer
     * @param {?} _element
     */
    constructor(_renderer, _element) {
        this._renderer = _renderer;
        this._element = _element;
    }
    /**
     * @return {?}
     */
    ngAfterContentInit() {
        this._lineSetter = new MatLineSetter(this._lines, this._renderer, this._element);
    }
}
MatGridTileText.decorators = [
    { type: Component, args: [{selector: 'mat-grid-tile-header, mat-grid-tile-footer',
                template: "<ng-content select=\"[mat-grid-avatar], [matGridAvatar]\"></ng-content><div class=\"mat-grid-list-text\"><ng-content select=\"[mat-line], [matLine]\"></ng-content></div><ng-content></ng-content>",
                changeDetection: ChangeDetectionStrategy.OnPush,
                encapsulation: ViewEncapsulation.None,
                preserveWhitespaces: false,
            },] },
];
/**
 * @nocollapse
 */
MatGridTileText.ctorParameters = () => [
    { type: Renderer2, },
    { type: ElementRef, },
];
MatGridTileText.propDecorators = {
    '_lines': [{ type: ContentChildren, args: [MatLine,] },],
};
function MatGridTileText_tsickle_Closure_declarations() {
    /** @type {?} */
    MatGridTileText.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatGridTileText.ctorParameters;
    /** @type {?} */
    MatGridTileText.propDecorators;
    /**
     *  Helper that watches the number of lines in a text area and sets
     * a class on the host element that matches the line count.
     * @type {?}
     */
    MatGridTileText.prototype._lineSetter;
    /** @type {?} */
    MatGridTileText.prototype._lines;
    /** @type {?} */
    MatGridTileText.prototype._renderer;
    /** @type {?} */
    MatGridTileText.prototype._element;
}
/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MatGridAvatarCssMatStyler {
}
MatGridAvatarCssMatStyler.decorators = [
    { type: Directive, args: [{
                selector: '[mat-grid-avatar], [matGridAvatar]',
                host: { 'class': 'mat-grid-avatar' }
            },] },
];
/**
 * @nocollapse
 */
MatGridAvatarCssMatStyler.ctorParameters = () => [];
function MatGridAvatarCssMatStyler_tsickle_Closure_declarations() {
    /** @type {?} */
    MatGridAvatarCssMatStyler.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatGridAvatarCssMatStyler.ctorParameters;
}
/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MatGridTileHeaderCssMatStyler {
}
MatGridTileHeaderCssMatStyler.decorators = [
    { type: Directive, args: [{
                selector: 'mat-grid-tile-header',
                host: { 'class': 'mat-grid-tile-header' }
            },] },
];
/**
 * @nocollapse
 */
MatGridTileHeaderCssMatStyler.ctorParameters = () => [];
function MatGridTileHeaderCssMatStyler_tsickle_Closure_declarations() {
    /** @type {?} */
    MatGridTileHeaderCssMatStyler.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatGridTileHeaderCssMatStyler.ctorParameters;
}
/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MatGridTileFooterCssMatStyler {
}
MatGridTileFooterCssMatStyler.decorators = [
    { type: Directive, args: [{
                selector: 'mat-grid-tile-footer',
                host: { 'class': 'mat-grid-tile-footer' }
            },] },
];
/**
 * @nocollapse
 */
MatGridTileFooterCssMatStyler.ctorParameters = () => [];
function MatGridTileFooterCssMatStyler_tsickle_Closure_declarations() {
    /** @type {?} */
    MatGridTileFooterCssMatStyler.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatGridTileFooterCssMatStyler.ctorParameters;
}
//# sourceMappingURL=grid-tile.js.map